import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { searchCurrentCourses } from '../../../store/courseService';
import { selectBarrierDetails } from '../../../store/dataSelectors';
import { loadBarrierNameDetails } from '../../../store/directusService';
import { selectCurrentParticipantBarriers } from '../../../store/participantSelectors';
import { loadParticipantBarriers } from '../../../store/participantService';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../utils/formValidation/commonConstants';
import { stableSort } from '../../../utils/sortFunctions';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import CurrentCourseTableRow from './CurrentCourseTableRow';

import app from '../../../app.module.css';

const headCells = [
    { id: 'id', numeric: false, label: 'ID' },
    { id: 'name', numeric: false, label: 'Name' },
    { id: 'startDate', numeric: false, label: 'Start Date' },
    { id: 'endDate', numeric: false, label: 'End Date' },
    { id: 'modules', numeric: false, label: 'Modules' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'name',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const CurrentCourseManagement = ({ isDisabled, isSelfEmployedCoach }) => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const [openId, setOpenId] = useState('');
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [currentCoursesSearchData, setCurrentCoursesSearchData] = useState({});

    // STORE STATE
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const currentCourses = useSelector((state) => state.entities.courseService.currentCourses);
    const currentCoursesMetaData = useSelector(
        (state) => state.entities.courseService.currentCoursesMetaData
    );
    const currentParticipantBarriers = useSelector(selectCurrentParticipantBarriers);
    const barrierNameDetails = useSelector(selectBarrierDetails);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );

    // USEEFFECTS
    useEffect(() => {
        if (
            !currentParticipantBarriers?.length ||
            currentParticipantBarriers?.participantId !== currentParticipant.id
        )
            dispatch(loadParticipantBarriers(currentParticipant.id));

        barrierNameDetails?.length < 1 && dispatch(loadBarrierNameDetails());
    }, []);

    useEffect(() => {
        if (currentParticipant?.id && currentParticipant?.serviceId) {
            const searchData = {
                active: true,
                serviceIds: [currentParticipant.serviceId],
                participantIds: [currentParticipant.id]
            };
            setCurrentCoursesSearchData(searchData);
            loadCurrentCourses(searchData);
        }
    }, [currentParticipant?.id, currentParticipant?.serviceId]);

    useEffect(() => {
        if (
            successMessage.startsWith('Module details for course') &&
            successMessage.endsWith('have been updated')
        ) {
            loadCurrentCourses(currentCoursesSearchData);
        }
    }, [successMessage]);

    // HELPER FNS
    const createRows = () =>
        stableSort(currentCourses, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <CurrentCourseTableRow
                key={el.id}
                row={el}
                toggleDropdown={(id) => setOpenId(id)}
                openId={openId}
                isDisabled={isDisabled}
                isSelfEmployedCoach={isSelfEmployedCoach}
            />
        ));

    const loadCurrentCourses = (searchData) => {
        if (!Object.keys(searchData).length) return;
        dispatch(searchCurrentCourses(searchData, 0));
    };

    // RENDER
    return (
        <div className={app.container}>
            <h2 className={app.sectionHeading}>Current Courses</h2>
            {currentCoursesMetaData?.last && currentCourses?.length < 1 ? (
                <LoadingSpinner content="No current courses found" />
            ) : (
                <ResultsTable
                    defaultOrderBy={'name'}
                    defaultRowsPerPage={rowMetaData.rowsPerPage}
                    headCells={headCells}
                    passRowMetaDataUp={setRowMetaData}
                    tableRows={createRows()}
                    totalResults={currentCoursesMetaData?.totalElements}
                    metaData={currentCoursesMetaData}
                />
            )}
        </div>
    );
};

CurrentCourseManagement.propTypes = {
    isDisabled: PropTypes.bool,
    isSelfEmployedCoach: PropTypes.bool
};

export default CurrentCourseManagement;
